import { Component, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { ViewChild, ElementRef } from '@angular/core';
import { Input, Output, EventEmitter }  from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';

import { User } from '@app/modules/model/user';
import { Place } from '@app/modules/model/place';
import { PlaceArea } from '@app/modules/model/placearea';
import { QrCode } from '@app/modules/model/qrcode';
import { DrawItem } from '@app/modules/model/draw';

import { alertService } from '@app/modules/common/alert';
import { languageService, languageSupport } from '@app/modules/common/language';
import { syncService } from '@app/modules/sync';
import { dataService } from '@app/modules/data';
import { viewService } from '@app/modules/view';

/************************************/
/* AREA COMPONENT                   */
/************************************/

@Component({
    selector: 'ui-area-view',
    templateUrl: 'ui-edit.html',
    styleUrls: [
      'ui-edit.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiAreaEdit extends languageSupport implements OnInit, OnChanges, OnDestroy {    
    @Input('place') _place: Place = null; 
    @Input('user') _user: User = null; 
    @Input('area') _area: PlaceArea = null; 

    get CanModify(){
        return this._place.AllowTableManage(this._user);
    }

    private _areaForm = null;   
    get AreaForm(){
        if (this._areaForm == null){
            this.LoadAreaForm();
        }
        return this._areaForm;
    }

    private get AreaDrawShape(){
        let _vertices = [];

        for(let _vertex of this._area.vertices){
            if ((_vertex.IsValid)){
                _vertices.push(_vertex);
            }
        }
        return _vertices;
    } 

    private get _AreaQrCodes(){
        let _qrcodes = [];

        for(let _qrcode of this._area.tables){
            if ((_qrcode.IsValid)){
                _qrcodes.push(_qrcode);
            }
        }
        return _qrcodes;
    }

    private get _AreaDrawItems(){
        let _items = [];

        for(let _item of this._area.items){
            if (_item.IsValid){
                _items.push(_item);
            }
        }

        return _items;
    }

    private ValidateDimension(control: FormControl): any {
        let _value = control.value;
        if (_value && (_value <= 0)){
            return { dimension: true };    
        }
        
        return null;
    }

    private LoadAreaForm(){
        let _name = this._area.name;
        let _vertex = this.AreaDrawShape;      // make a copy of the vertex 
        let _tables = this._AreaQrCodes;       // make a copy of the tables
        let _items = this._AreaDrawItems;      // make a copy of the drawing items
        let _width = this._area.width;
        let _height = this._area.height;

        if (!this._areaForm){
            this._areaForm = new FormGroup({
                name: new FormControl(_name, [
                    Validators.required
                ]),
                vertex: new FormControl(_vertex, [
                    // no validators required
                ]),     
                tables: new FormControl(_tables, [
                    Validators.required
                ]),
                items: new FormControl(_items, [
                    // no validators required
                ]),           
                width: new FormControl(_width, [
                    Validators.required,
                    this.ValidateDimension.bind(this)
                ]),
                height: new FormControl(_height, [
                    Validators.required,
                    this.ValidateDimension.bind(this)
                ])
            });
        }
        else {
            this._areaForm.patchValue({
                name: _name,
                vertex: _vertex, 
                tables: _tables,
                items: _items,
                width: _width,
                height: _height
            }); 
        }
    
        this._areaForm.markAsPristine();
        setTimeout(() => {
            this._areaForm.markAsPristine();
        }, 0);        
    }

    private _inrefresh = false;
    DoAreaReload(){
        if (this._inrefresh){
            return;     // avoid recursive call -> refresh -> onchange -> refresh
        }

        this._inrefresh = true;
        
        this._area.width = this.AreaForm.get('width').value;
        this._area.height = this.AreaForm.get('height').value;

        this._area.DoRefresh('PLACEAREA');
        this.change.markForCheck();
        this._inrefresh = false;
    }

    OnAreaRefresh(){
        if (this._inrefresh){
            return;     // avoid recursive call -> refresh -> onchange -> refresh
        }

        this._inrefresh = true;
        
        this._area.DoRefresh('PLACEAREA');
        this.AreaForm.markAsDirty();

        this._inrefresh = false;
    }

    OnAreaReload(){
        this.LoadAreaForm();
        this.change.markForCheck();
    }

    get AreaVertices(){
        let _vertices = this.AreaForm.get('vertex').value;
        _vertices.sort((a, b) => {
            return parseInt(a.sort) - parseInt(b.sort);
        })
        return _vertices;
    }

    get AreaTables() {
        let _tables = this.AreaForm.get('tables').value;
        _tables.sort((a, b) => {
            return  parseInt(a.number) - parseInt(b.number);
        })
        return _tables;
    }

    get AreaItems(){
        let _items = this.AreaForm.get('items').value;
        _items.sort((a, b) => {
            return (a.type > b.type) ? 1 : (a.type < b.type) ? -1 : 0; 
        })
        return _items;
    }

    /***********************************/
    /* STARTS HERE                     */
    /***********************************/

    private _close_subscription = null;
    private _area_subscriptions = [];
    private _draw_subscriptions = [];

    constructor(private lang: languageService, private change: ChangeDetectorRef, private alertCtrl: alertService, private sync: syncService, private data: dataService, public view: viewService) {
        super(lang, change);
    }

    private _CanClose(){
        return new Promise(async (resolve) => {
            let _form = this.AreaForm;
            if (_form.dirty){
                console.log("[EDIT AREA] confirm changes lost");
                const alert = this.alertCtrl.alert({
                    header: this.lang.tr('@changes_lost_confirm_tittle'),
                    message: this.lang.tr('@changes_lost_confirm_message'),
                    backdropDismiss: false,
                    buttons: [{
                        text: this.lang.tr('@yes'),
                        handler: () => {
                            setTimeout(() => {
                                alert.dismiss(true);
                            }, 100);
    
                            return false;
                        }
                    }, {
                        text: this.lang.tr('@no'),
                        handler: () => {
                            setTimeout(() => {
                                alert.dismiss(false);
                            }, 100);
    
                            return false;
                        }
                    }]
                });
    
                alert.onDidDismiss().then((detail:any) => {
                    if (detail.data){
                        _form.markAsPristine();
                    }
                    
                    resolve(detail.data);
                });
    
                await alert.present();        
            }
            else {
                resolve(true);
            }
        });
    }

    private _backup = {
        placearea: null,    // this area information
        tablareas: null     // all tables area information
    };

    private _SaveBackup(){
        this._backup.tablareas = new Map <QrCode, PlaceArea>()
        for(let _qrcode of this._place.tables){
            if (_qrcode.IsValid){
                this._backup.tablareas.set(_qrcode, _qrcode.area);
            }
        }

        this._backup.placearea = this._area.Copy();
    }

    private _LoadBackup(){
        let _bkarea = this._backup.placearea;

        // recover the tables area references
        for(let [_qrcode, _qrarea] of this._backup.tablareas){
            _qrcode.area = _qrarea;
        }

        // remove the current drawable information
        for(let _draw of this._area.drawable){
            _draw.status = 'DE';    
        }

        // recover the placearea information
        this._area.Data = _bkarea.Info;

        // recover the drawables information
        let _drawables = _bkarea.drawable.slice(0);
        for(let _draw of _drawables){
            if (_draw.CopyOf){
                _draw.Overwrite();
            }
        }

        this._area.DoRefresh('PLACEAREA');
    }

    /* REGISTER TO CLICK EVENTS */

    @ViewChild('container', {static: false}) _container: ElementRef;
    private RefreshDrawables(){
        for (let _subscription of this._draw_subscriptions){
            _subscription.unsubscribe();
            _subscription = null;
        }
        this._draw_subscriptions = [];

        for (let _draw of this._area.drawable){
            this._draw_subscriptions.push(
                _draw.OnDrag.subscribe(
                data => {
                    let _dom = document.getElementById("drawitem_" + _draw.objref);
                    if (_dom){
                        let _scrollable = this._container?.nativeElement;
                        if (_scrollable){
                            let _domRect = _dom.getBoundingClientRect();
                            let _scrRect = _scrollable.getBoundingClientRect();
                    
                            _scrollable.scrollTop = (_scrollable.scrollTop + _domRect.top - _scrRect.top);;

                            _dom.classList.add('scrollto');
                            this.change.markForCheck();
    
                            setTimeout(() => {
                                _dom.classList.remove('scrollto');
                                this.change.markForCheck();
                            }, 300);    
                        }
                    }
                })
            );
        }
    }

    ngOnInit() {
        this._close_subscription = this.view.OnCloseRequest.subscribe(
        async () => {
            this.view.OnCloseResponse(await this._CanClose());
        });   
    }

    private _rendered: PlaceArea = null;
    private set objectrender(value){
        if ((this._rendered != null) && (value == true)){
            return;     // no efect
        }

        if ((this._rendered == null) && (value != true)){
            return;     // no efect
        }
        
        if (value == true){ 
            this._rendered = this._area;
        }

        for (let _draw of this._rendered.drawable){
            _draw.render = value;
        }

        this._rendered.DoRefresh('PLACEAREA');
        if (value == false){
            this._rendered = null;
        }
    }

    ngOnChanges() {
        this._SaveBackup();             // save the current status

        for (let _subscription of this._area_subscriptions){
            _subscription.unsubscribe();
            _subscription = null;
        }
        this._area_subscriptions = [];

        this.objectrender = false;      // stop rendering previous
        if (this._area){
            this.RefreshDrawables();

            this._area_subscriptions.push(
                this._area.OnRefresh.subscribe(
                data => {
                    this.RefreshDrawables();
                    this.change.markForCheck();
                })
            );

            for (let _table of this._area.tables){
                this._area_subscriptions.push(
                    _table.OnRefresh.subscribe(
                    data => {
                        this.change.markForCheck();
                    })
                );
            }

            this.objectrender = true;   // render object position        
        }

        this.LoadAreaForm();
    }

    ngOnDestroy() {
        super.OnDestroy();

        for (let _subscription of this._area_subscriptions){
            _subscription.unsubscribe();
            _subscription = null;
        }
        this._area_subscriptions = [];

        for (let _subscription of this._draw_subscriptions){
            _subscription.unsubscribe();
            _subscription = null;
        }
        this._draw_subscriptions = [];

        if (this._close_subscription){
            this._close_subscription.unsubscribe();
            this._close_subscription = null;
        }

        this.objectrender = false;   // stop rendering
    }

    /***********************************/
    /* EVENT HANDLERS                  */
    /***********************************/

    @Output('onPanel') _onPanel = new EventEmitter<any>();     
    OnPanel(panel){
        this._onPanel.emit(panel);
    }

    private async _OnClose(iscancel){
        if (this._close_subscription){
            this._close_subscription.unsubscribe();
            this._close_subscription = null;
        }

        if (iscancel){
            this._LoadBackup();
        }

        let _promise = new Promise((resolve) => {
            if (this.view.RightPanel){
                this._onPanel.emit({
                    scnd: null,
                    args: null
                }); 
                
                resolve(true);
            }
            else {
                this._onPanel.emit({
                    scnd: null
                });
    
                // wait for animation to finish
                setTimeout(() => {
                    this._onPanel.emit({
                        args: null
                    });    

                    resolve(true);
                }, 300);
            }    
        });

        await _promise;
    }

    async OnClose(){
        if (this.AreaForm.dirty){
            console.log("[EDIT AREA] confirm changes lost");
            const alert = this.alertCtrl.alert({
                header: this.lang.tr('@changes_lost_confirm_tittle'),
                message: this.lang.tr('@changes_lost_confirm_message'),
                backdropDismiss: false,
                buttons: [{
                    text: this.lang.tr('@yes'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(true);
                        }, 100);

                        return false;
                    }
                }, {
                    text: this.lang.tr('@no'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(false);
                        }, 100);

                        return false;
                    }
                }]
            });

            alert.onDidDismiss().then((detail:any) => {
                if (detail.data == true){
                    this._OnClose(true);
                }
            });

            await alert.present();        
        }
        else {
            this._OnClose(true);
        }
    }

    get CanRemoveArea(){
        if (!this._area.IsValid || this._area.ToInsert){
            return false;
        }
        return true;
    }

    async OnRemoveArea(){
        const alert = this.alertCtrl.alert({
            header: this.lang.tr('@place_area_delete_confirm'),
            message: this.lang.tr('@place_area_delete_message', [ this._area.name ]),
            buttons: [{
                text: this.lang.tr('@yes'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(true);
                    }, 100);
                    return false;
                }
            }, {
                text: this.lang.tr('@no'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(false);
                    }, 100);
                    return false;
                }
            }]
        });

        alert.onDidDismiss().then(async (detail:any) => {
            if (detail.data == true){
                this._area.status = 'DE';
            }

            this.AreaForm.markAsPristine();
            if (await this._area.DoCommit()){
                this._OnClose(false);
            }
            
            this.RefreshDrawables();
        });

        await alert.present();        
    }

    async OnAccept(){
        this._area.name = this.AreaForm.get('name').value;
        this._area.width = this.AreaForm.get('width').value;
        this._area.height = this.AreaForm.get('height').value;

        this.AreaForm.markAsPristine();
        if (await this._place.DoCommit()){
            this._OnClose(false);
        }
    }    

    /* VERTEX (SHAPE) MANAGEMENT */

    private _vertexForm = null;   
    get VertexForm(){
        if (this._vertexForm == null){
            this.LoadVertexForm();
        }
        return this._vertexForm;
    }

    private LoadVertexForm(){
        let _v_order = this._area.vertices.length;
        let _coord_x = 0;
        let _coord_y = 0;

        if (!this._vertexForm){
            this._vertexForm = new FormGroup({
                v_order: new FormControl(_v_order, [
                    Validators.required
                ]),
                coord_x: new FormControl(_coord_x, [
                    Validators.required
                ]),
                coord_y: new FormControl(_coord_y, [
                    Validators.required
                ])
            });
        }
        else {
            this._vertexForm.patchValue({
                v_order: _v_order,
                coord_x: _coord_x, 
                coord_y: _coord_y
            }); 
        }
    
        this._vertexForm.markAsPristine();
        setTimeout(() => {
            this._vertexForm.markAsPristine();
        }, 0);   
        
        this.change.markForCheck();
    }

    private _drawvertex: Array<DrawItem> = [];

    OnDrawVertex(vertex){
        let _pos = this._drawvertex.indexOf(vertex);
        if (_pos != -1){
            this._drawvertex.splice(_pos, 1);
        }
        else {
            this._drawvertex.push(vertex);
        }
    }

    IsDrawVertex(vertex){
        if (vertex != null){
            return (this._drawvertex.indexOf(vertex) != -1);
        }
        return false;
    }

    private _vertexeditor = false
    get ShapeChooser(){
        return this._vertexeditor;
    }

    OnShowVertex(){
        this._vertexeditor = true;
        this.LoadVertexForm();
    }

    OnHideVertex(){
        this._vertexeditor = false;
        this.change.markForCheck();        
    }    

    get Vmin(){
        return 1;
    }

    get Vmax(){
        return this._area.vertices.length;
    }

    ValidIndex(_index){
        if ((_index.sort < this.Vmin) || (_index.sort > this.Vmax)) {
            return false;
        }

        return true;
    }

    OnAddVertex(){
        let _sort = parseInt(this.VertexForm.get('v_order').value);
        if (!this.ValidIndex(_sort)){
            return;
        }

        this.OnHideVertex();

        // make a gap for this vertex
        for (let _vertex of this._area.vertices){
            if (_vertex.IsValid && (_vertex.sort >= _sort)){
                _vertex.sort = _vertex.sort + 1;
            }
        }

        let _draw = new DrawItem(null, this.data);
        if (_draw){
            _draw.render = true;
            _draw.status = 'AC';
            _draw.type = 'VERTEX';
            _draw.area = this._area;
            _draw.sort = _sort;
            _draw.coordinates = {
                x: parseInt(this.VertexForm.get('coord_x').value), 
                y: parseInt(this.VertexForm.get('coord_y').value), 
                w: 0,
                h: 0,
                r: 0
            }
        }

        let _vertices = this.AreaForm.get('vertex').value;
        _vertices.push(_draw);       
        this.AreaForm.get('vertex').setValue(_vertices);
        this.AreaForm.markAsDirty();        

        this._area.AddDrawable(_draw);
    }

    OnDelVertex(vertex){
        vertex.status = 'DE';

        let _vertices = this.AreaForm.get('vertex').value;
        let _pos = _vertices.indexOf(vertex);
        if (_pos != -1){
            _vertices.splice(_pos, 1);
        }

        this.AreaForm.get('vertex').setValue(_vertices);        
        this.AreaForm.markAsDirty();

        // remove the gap for this vertex
        for (let _vertex of this._area.vertices){
            if (_vertex.IsValid && (_vertex.sort >= vertex.sort)){
                _vertex.sort = _vertex.sort - 1;
            }
        }

        this._area.DoRefresh('PLACEAREA');
    }

    /* TABLE MANAGEMENT */

    private _selectedtables = null;
    get SelectTables(){
        return this._selectedtables;
    }

    OnShowTables(){
        this._selectedtables = this.AreaForm.get('tables').value || [];;
        this.change.markForCheck();
    }

    OnHideTables(){
        this._selectedtables = null;
        this.change.markForCheck();
    }

    private _TableToArea(_table){
        if (!_table.draw || !_table.draw.IsValid){
            let _draw = new DrawItem(null, this.data);
            if (_draw){
                _draw.status = 'AC';
                _draw.type = 'TABLE';
                _draw.shape = 'SQUARE';
                _draw.coordinates = {
                    x: 10, y: 10, w: 90, h: 90, r: 0
                }    
            }

            _table.draw = _draw;
        }

        _table.area = this._area;
        _table.draw.render = true;

        this._area.AddDrawable(_table.draw);
    }

    OnAddTables(tables){
        this.OnHideTables();

        let _tables = this.AreaForm.get('tables').value;
        for(let _table of _tables){
            if (tables.indexOf(_table) == -1){
                _table.area = null;         // remove tha area information for table

                if (_table.status == 'PA'){
                    _table.status = 'DE';   // this table is not commited and has been deleted
                }
            }
        }

        // ensure drawinfo on all provided tables
        for(let _table of tables){
            this._TableToArea(_table);
        }

        this.AreaForm.get('tables').setValue(tables);
        this.AreaForm.markAsDirty();

        this._selectedtables = null;
        this._area.DoRefresh('PLACEAREA');
    }

    /* ITEM MANAGEMENT */

    private _drawitem: Array<DrawItem> = [];

    OnDrawView(item){
        let _pos = this._drawitem.indexOf(item);
        if (_pos != -1){
            this._drawitem.splice(_pos, 1);
        }
        else {
            this._drawitem.push(item);
        }
    }

    IsDrawView(item){
        if (item != null){
            return (this._drawitem.indexOf(item) != -1);
        }
        return false;
    }

    private _selecteditems = null;
    get SelectItems(){
        return this._selecteditems;
    }

    get ItemChooser(){
        return (this._selecteditems !== null);
    }

    OnShowItems(){
        this._selecteditems = this.AreaForm.get('items').value || [];;
        this.change.markForCheck();
    }

    OnHideItems(){
        this._selecteditems = null;
        this.change.markForCheck();        
    }

    get ItemTypes(){
        return [ 'BAR', 'BILLIARD', 'FOOTBALL', 'DOOR', 'WINDOW', 'STAIRS', 'SOFA', 'GENERIC' ];
    }

    ItemPhoto(type){
        let _urls = {
            BAR: '/assets/image/item-bar.svg',
            BILLIARD: '/assets/image/item-billiard.svg',
            FOOTBALL: '/assets/image/item-football.svg',
            DOOR: '/assets/image/item-door.svg',
            WINDOW: '/assets/image/item-window.svg',
            STAIRS: '/assets/image/item-stairs.svg',
            SOFA: '/assets/image/item-sofa.svg',
            GENERIC: '/assets/image/item-generic.svg'
        }

        if (type in _urls){
            return _urls[type];
        }

        return null;
    }

    ItemSize(type){
        let _size = 0;
        
        if (this.SelectItems){
            for (let _item of this.SelectItems){
                if ((_item.type == type) && (_item.IsValid)){
                    _size += 1;
                }
            }    
        }

        return _size;
    }

    private ItemDim(type, ){
        switch(type){
            case 'BILLIARD':
                return [ 213, 107 ];     // cm: standard width, height of billiard table
            case 'FOOTBALL':
                return [ 137, 82 ];      // cm: standard width, height of football table
        }

        return [ null, null ];
    }

    OnAddItem(type){
        this.OnHideItems();

        let _shape = null;
        if (type == 'BAR'){
            _shape = 'LINEAR';
        }

        if (type == 'DOOR'){
            _shape = 'TOLEFT';
        }

        if (type == 'STAIRS'){
            _shape = 'SIMPLE';
        }

        if (type == 'GENERIC'){
            _shape = 'GENRCT';
        }

        let _draw = new DrawItem(null, this.data);
        if (_draw){
            _draw.render = true;
            _draw.status = 'AC';
            _draw.type = type;
            _draw.area = this._area;
            _draw.shape = _shape;
            _draw.coordinates = {
                x: 0, 
                y: 0, 
                w: this.ItemDim(type)[0], 
                h: this.ItemDim(type)[1], 
                r: 0
            }
        }

        let _items = this.AreaForm.get('items').value;
        _items.push(_draw);
        this.AreaForm.get('items').setValue(_items);
        
        this.AreaForm.markAsDirty();        
        this._area.AddDrawable(_draw);
    }

    OnDelItem(item){
        item.status = 'DE';

        let _items = this.AreaForm.get('items').value;
        let _pos = _items.indexOf(item);
        if (_pos != -1){
            _items.splice(_pos, 1);
        }
        this.AreaForm.get('items').setValue(_items);        
        
        this.AreaForm.markAsDirty();
        this._area.DoRefresh('PLACEAREA');
    }
}
